import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, Hash, Phone, House as HouseIcon, Car,
  Vault, Calendar, Compass, Heart, Star, ChartBar, ArrowDown, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import { useSavedReportLoader } from "../lib/useSavedReport";
import { useAuth } from "../context/AuthContext";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";

export default function DeepNumerologyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", birth_date: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // Auto-prefill from signed-in user's profile (one-shot via shared hook)
  useBirthDetailsPrefill((bd, u) => {
    setForm((prev) => ({
      name: prev.name || u?.name || "",
      birth_date: prev.birth_date || bd?.date || "",
    }));
  });

  // For paywall: derive a birthDetails shape so the fingerprint is stable
  const paywallBirthDetails = useMemo(() => ({
    date: form.birth_date,
    time: "00:00",
    place: form.name.trim().toLowerCase(),
    timezone: "Asia/Kolkata",
  }), [form.birth_date, form.name]);

  useSavedReportLoader({
    expectedTool: "numerology_deep",
    setForm: (bd) => {
      // saved-report birth_details lookalike
      if (bd?.date) setForm((f) => ({ ...f, birth_date: bd.date }));
      if (bd?.place) setForm((f) => ({ ...f, name: bd.place }));
    },
    setResult,
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.birth_date) {
      toast.error("Please enter your full name and date of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/numerology-deep/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='dn-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your numerology. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="numerology-deep-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="dn-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Hash size={14} weight="duotone" /> Free preview · Chaldean + Lo Shu Grid + Lucky Numbers
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Deep <span className="italic text-gold">Numerology</span> & Lo Shu Grid
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Your name and date of birth aren't random — they encode a numeric blueprint your planets honour. We compute your Driver, Conductor, Soul, Personality, Name and Maturity numbers (Chaldean), map them onto the Saturn-square <b className="text-[#3F2D1B]">Lo Shu Grid</b>, detect karmic-debt and master-number signatures, then prescribe your <b className="text-[#3F2D1B]">lucky mobile number patterns, house numbers, vehicle numbers, locker PINs</b> and a colour-coded 12-month theme calendar.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Your full name (as commonly used)</span>
              <input type="text" required value={form.name} onChange={handle("name")} placeholder="Suresh Kumar" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="dn-name" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.birth_date} onChange={handle("birth_date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="dn-date" />
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="dn-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Decoding your numeric blueprint..." : "Reveal my numerology + Lo Shu Grid"}
          </button>
        </form>

        {result && (
          <div className="mt-10 space-y-6" data-testid="dn-result">
            {/* Hero */}
            <HeroBlock cn={result.core_numbers} />

            {/* 6 core number cards */}
            <CoreNumbersRow cn={result.core_numbers} />

            {/* Lo Shu Grid — FREE */}
            <LoShuGridSection loshu={result.loshu} />

            {/* Premium */}
            <PaywallGate
              tool="numerology_deep"
              toolLabel="Deep Numerology & Lo Shu Grid"
              birthDetails={paywallBirthDetails}
              report={result}
            >
              <div className="space-y-6">
                {/* Karmic + master */}
                {(result.karmic_debts.length > 0 || result.master_numbers.length > 0) && (
                  <KarmicMasterPanel kd={result.karmic_debts} mn={result.master_numbers} />
                )}

                {/* Lucky numbers */}
                <LuckyNumbersPanel lucky={result.lucky} nameAdvice={result.name_advice} />

                {/* Personal year + 12-month calendar */}
                <ForecastPanel pc={result.personal_cycle} forecast={result.forecast_12_months} />

                {/* Compatibility row */}
                <CompatibilityRow data={result.compatibility} driver={result.core_numbers.driver.value} />

                {/* Loshu missing-digit remedies */}
                {result.loshu.missing_remedies?.length > 0 && (
                  <MissingRemediesPanel items={result.loshu.missing_remedies} />
                )}
              </div>
            </PaywallGate>

            {/* CTA — Pythagorean numerology specialist */}
            <ToolAstrologerCta
              requiredTechnique="Chaldean Numerology"
              pitch={`Get a live read — Personal Year ${result.personal_cycle.year.value} ${result.personal_cycle.year.theme}, karmic-debt timing & a name-correction blueprint`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- Sub-components ----------

function HeroBlock({ cn }) {
  const drv = cn.driver;
  return (
    <div
      className="rounded-2xl text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40"
      style={{ background: `linear-gradient(135deg, ${drv.hex_dark || "#3F2D1B"} 0%, #3F2D1B 60%, ${drv.color || "#7c4f2a"} 100%)` }}
      data-testid="dn-hero"
    >
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
        <Sparkle size={14} weight="fill" /> Your numeric DNA
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Driver <span className="italic text-amber-300">{drv.value}</span> · <span className="text-amber-200/95 text-2xl sm:text-3xl">{drv.title}</span>
          </h2>
          <div className="mt-2 text-amber-100/90 text-sm">
            Ruled by <b className="text-amber-300">{drv.planet}</b> · Conductor <b className="text-amber-300">{cn.conductor.value}</b> ({cn.conductor.planet})
          </div>
          <p className="mt-4 text-amber-50/95 text-sm sm:text-base leading-relaxed max-w-2xl">{drv.vibe}</p>
        </div>
        <div className="text-5xl sm:text-7xl font-serif-display opacity-90 text-amber-200" aria-hidden>
          {drv.value}
        </div>
      </div>
    </div>
  );
}

function CoreNumbersRow({ cn }) {
  const items = [
    { key: "driver", label: "Driver (Mulank)", num: cn.driver.value, hint: cn.driver.planet, color: cn.driver.color },
    { key: "conductor", label: "Conductor (Bhagyank)", num: cn.conductor.value, hint: cn.conductor.planet, color: cn.conductor.color },
    { key: "name", label: "Name (Namank)", num: cn.name.value, hint: "Chaldean", color: "#52B788" },
    { key: "soul", label: "Soul (Heart's Desire)", num: cn.soul.value, hint: "Vowels-only", color: "#F77F00" },
    { key: "personality", label: "Personality", num: cn.personality.value, hint: "Consonants", color: "#48BFE3" },
    { key: "maturity", label: "Maturity", num: cn.maturity.value, hint: "After ~35", color: "#7B2CBF" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dn-core">
      {items.map((it) => (
        <div
          key={it.key}
          className="rounded-2xl p-4 text-center border"
          style={{ backgroundColor: `${it.color}1A`, borderColor: `${it.color}55` }}
          data-testid={`dn-core-${it.key}`}
        >
          <div className="text-[10px] uppercase tracking-widest font-medium" style={{ color: it.color }}>{it.label}</div>
          <div className="font-serif-display text-4xl sm:text-5xl mt-1" style={{ color: it.color }}>{it.num || "—"}</div>
          <div className="text-[10px] text-[#3F2D1B]/65 mt-1">{it.hint}</div>
        </div>
      ))}
    </div>
  );
}

function LoShuGridSection({ loshu }) {
  // The 3x3 grid uses the digit-profile colour for each cell, scaled by count
  const PLANET_COLOR = {
    1: "#FFB703", 2: "#48BFE3", 3: "#F9C74F", 4: "#4361EE", 5: "#52B788",
    6: "#F77F00", 7: "#7B2CBF", 8: "#2C3E50", 9: "#C0392B",
  };
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="dn-loshu">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
        <ChartBar size={14} weight="duotone" /> Lo Shu Grid · digit frequencies + active planes
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* 3x3 grid */}
        <div className="flex flex-col items-center" data-testid="dn-loshu-grid">
          <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 rounded-2xl bg-[#FAF4E8] border-2 border-amber-300/40">
            {loshu.cells.flat().map((cell, i) => {
              const color = PLANET_COLOR[cell.digit];
              const present = cell.count > 0;
              const intensity = present ? 0.18 + Math.min(cell.count, 4) * 0.18 : 0;
              return (
                <div
                  key={i}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex flex-col items-center justify-center relative transition-all"
                  style={{
                    backgroundColor: present ? `${color}${Math.round(intensity * 255).toString(16).padStart(2, "0")}` : "#FFFFFF",
                    border: `2px ${present ? "solid" : "dashed"} ${present ? color : "#E5DBC4"}`,
                  }}
                  data-testid={`dn-loshu-cell-${cell.digit}`}
                >
                  <span className="font-serif-display text-2xl sm:text-3xl" style={{ color: present ? color : "#3F2D1B33" }}>
                    {cell.digit}
                  </span>
                  <span className="text-[10px] mt-1" style={{ color: present ? color : "#9B8C76" }}>
                    {present ? "×".repeat(Math.min(cell.count, 4)) + (cell.count > 4 ? `×${cell.count}` : "") : "missing"}
                  </span>
                  <span className="absolute top-0.5 right-1 text-[8px] uppercase tracking-widest" style={{ color: present ? color : "transparent" }}>{cell.planet?.slice(0, 3)}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-muted mt-3 italic text-center max-w-xs">
            Saturn-square layout. Count includes your driver + conductor. The brighter and bolder a cell, the more dominant that energy is in your life.
          </p>
        </div>

        {/* Active + Inactive planes */}
        <div className="space-y-3">
          <div>
            <div className="label-eyebrow !text-[10px] text-emerald-700 mb-1.5">Active Loshu planes (yogas)</div>
            {loshu.active_planes.length === 0 ? (
              <p className="text-xs text-muted italic">No fully-active planes — explore the missing-digit remedies below to activate them.</p>
            ) : (
              <ul className="space-y-1.5">
                {loshu.active_planes.map((p) => (
                  <li key={p.name} className="rounded-lg px-3 py-2 bg-emerald-50/70 border border-emerald-200 text-xs" data-testid={`dn-plane-${p.name.replace(/ /g, "-")}`}>
                    <div className="flex items-baseline gap-2">
                      <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                      <span className="font-medium text-emerald-900">{p.name}</span>
                      <span className="text-[10px] font-mono text-emerald-700/60">{p.digits.join("-")}</span>
                    </div>
                    <p className="text-emerald-900/80 mt-0.5 leading-snug">{p.meaning}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {loshu.missing_digits.length > 0 && (
            <div>
              <div className="label-eyebrow !text-[10px] text-rose-700 mb-1.5">Missing digits</div>
              <div className="flex flex-wrap gap-1.5">
                {loshu.missing_digits.map((d) => (
                  <span key={d} className="text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-900 border border-rose-200 font-mono">
                    {d}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-muted mt-1.5">See "Missing-digit remedies" below for fixes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KarmicMasterPanel({ kd, mn }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dn-karmic-master">
      {kd.length > 0 && (
        <div className="card-surface rounded-2xl p-5 bg-rose-50/60 border border-rose-200">
          <div className="label-eyebrow text-rose-800 mb-2">Karmic debts in your blueprint</div>
          <ul className="space-y-2">
            {kd.map((k) => (
              <li key={k.number} className="text-sm text-rose-950 leading-snug">{k.meaning}</li>
            ))}
          </ul>
        </div>
      )}
      {mn.length > 0 && (
        <div className="card-surface rounded-2xl p-5 bg-amber-50 border border-amber-200">
          <div className="label-eyebrow text-amber-800 mb-2">Master numbers</div>
          <ul className="space-y-2">
            {mn.map((m) => (
              <li key={m.number} className="text-sm text-amber-950 leading-snug">{m.meaning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function LuckyNumbersPanel({ lucky, nameAdvice }) {
  const items = [
    { icon: Phone, label: "Mobile last 4-digit", items: lucky.mobile_last_4, color: "#52B788", testid: "dn-lucky-mobile" },
    { icon: HouseIcon, label: "House numbers", items: lucky.house_numbers.map(String), color: "#F77F00", testid: "dn-lucky-house" },
    { icon: Car, label: "Vehicle numbers", items: lucky.vehicle_numbers.map(String), color: "#4361EE", testid: "dn-lucky-vehicle" },
    { icon: Vault, label: "Locker / Bank PIN", items: lucky.locker_pins, color: "#7B2CBF", testid: "dn-lucky-locker" },
  ];
  return (
    <div className="space-y-4" data-testid="dn-lucky">
      <div className="card-surface rounded-2xl p-5 sm:p-7">
        <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
          <Star size={14} weight="duotone" /> Your lucky number recommendations
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map(({ icon: Icon, label, items: list, color, testid }) => (
            <div key={label} className="rounded-xl border p-4" style={{ backgroundColor: `${color}10`, borderColor: `${color}55` }} data-testid={testid}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} weight="duotone" style={{ color }} />
                <span className="text-sm font-medium text-[#3F2D1B]">{label}</span>
              </div>
              {list.length === 0 ? (
                <p className="text-xs text-muted italic">No suggestions found.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {list.map((s) => (
                    <span
                      key={s}
                      className="text-xs px-2 py-1 rounded-md font-mono font-medium"
                      style={{ backgroundColor: color, color: "#FAF4E8" }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted mt-3 italic">
          A "lucky" mobile/house/vehicle number is one whose digits sum to your Driver or Conductor (reduce repeatedly).
        </p>
      </div>

      {nameAdvice && (
        <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid="dn-name-advice">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="label-eyebrow text-gold">Your name vs your destiny</div>
            <div className="text-[10px] font-mono text-[#3F2D1B]/65">
              Current: <b className="text-[#3F2D1B]">{nameAdvice.current}</b> · Target: <b className="text-[#3F2D1B]">{nameAdvice.targets.join(" or ")}</b>
            </div>
          </div>
          {/* Score bar */}
          <div className="mt-3">
            <div className="h-3 rounded-full bg-[#FAF4E8] overflow-hidden border border-amber-300/40">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${nameAdvice.score}%`,
                  background: nameAdvice.score >= 75 ? "#52B788"
                    : nameAdvice.score >= 50 ? "#FFB703"
                    : "#C0392B",
                }}
              />
            </div>
            <div className="text-sm font-medium text-[#3F2D1B] mt-2">
              <span className="font-serif-display text-2xl mr-2">{nameAdvice.score}%</span>
              {nameAdvice.verdict}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ForecastPanel({ pc, forecast }) {
  // Theme → tint
  const themeColor = {
    1: "#FFB703", 2: "#48BFE3", 3: "#F9C74F", 4: "#4361EE", 5: "#52B788",
    6: "#F77F00", 7: "#7B2CBF", 8: "#2C3E50", 9: "#C0392B",
  };
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="dn-forecast">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
        <Calendar size={14} weight="duotone" /> Personal Year {pc.year.value} — {pc.year.theme}
      </div>
      <p className="text-sm text-[#3F2D1B] leading-relaxed mb-4">{pc.year.blurb}</p>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {forecast.map((m, i) => {
          const color = themeColor[m.personal_month] || "#9B8C76";
          return (
            <div
              key={i}
              className="rounded-lg p-3 border text-center"
              style={{ backgroundColor: `${color}1A`, borderColor: `${color}55` }}
              data-testid={`dn-month-${i}`}
            >
              <div className="text-[10px] uppercase tracking-widest font-medium" style={{ color }}>{m.year_month}</div>
              <div className="font-serif-display text-2xl mt-0.5" style={{ color }}>{m.personal_month}</div>
              <div className="text-[10px] text-[#3F2D1B] leading-snug mt-1">{m.theme}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompatibilityRow({ data, driver }) {
  const toneStyle = {
    friend:    { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900" },
    neutral:   { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-900"   },
    challenge: { bg: "bg-rose-50",    border: "border-rose-300",    text: "text-rose-900"    },
    self:      { bg: "bg-violet-50",  border: "border-violet-300",  text: "text-violet-900"  },
  };
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="dn-compatibility">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
        <Heart size={14} weight="duotone" /> Driver compatibility — how Driver {driver} feels next to others
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
        {data.map((c) => {
          const st = toneStyle[c.tone] || toneStyle.neutral;
          return (
            <div key={c.other} className={`rounded-lg p-2.5 border ${st.bg} ${st.border}`} data-testid={`dn-compat-${c.other}`}>
              <div className={`font-serif-display text-2xl text-center ${st.text}`}>{c.other}</div>
              <div className={`text-[9px] text-center uppercase tracking-widest mt-0.5 ${st.text}`}>{c.tone}</div>
              <div className="text-[10px] text-center text-[#3F2D1B]/70 mt-0.5 leading-tight">{c.title}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MissingRemediesPanel({ items }) {
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7 bg-rose-50/30 border border-rose-200" data-testid="dn-remedies">
      <div className="label-eyebrow text-rose-800 flex items-center gap-1.5 mb-3">
        <Compass size={14} weight="duotone" /> Loshu missing-digit remedies
      </div>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.digit} className="rounded-xl bg-white/70 border border-rose-200 p-4">
            <div className="flex items-baseline gap-2">
              <span className="font-serif-display text-2xl text-rose-700">{r.digit}</span>
              <span className="font-medium text-sm text-[#3F2D1B]">{r.theme}</span>
            </div>
            <p className="text-xs text-[#3F2D1B]/80 mt-1.5 leading-snug">{r.missing_effect}</p>
            <p className="text-xs mt-2 text-emerald-800 leading-snug"><b>Remedy:</b> {r.remedy}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
